import styled from '@emotion/styled';
import { EffectTriggerEvent } from '@hyperconnect/effects/dist/EffectRenderer';
import * as Sentry from '@sentry/nextjs';
import { atom } from 'jotai';
import { i18n } from 'next-i18next';
import StudioGraphicFillIcon from 'src/assets/images/icStudioGraphicFill.svg';
import { isGestureEffect, isBeautyEffect, isGestureEffectGroup } from 'src/components/Effects/utils';
import WebToAppModal from 'src/components/WebToAppModal';
import { isCreatorModeAtom } from 'src/stores/creator/atom';
import { eventMatchPageAtom, handleMyStreamVideoAtom, matchInfoAtom, statusAtom } from 'src/stores/match/atoms';
import { openedModalsAtom, showModalAtom } from 'src/stores/modal/atoms';
import { showCommonErrorToastAtom, showToastAtom } from 'src/stores/toast/atoms';
import { DecoGroupName, DecoUnsupportReason, DecoEffect, BeautyEffect, DecoEffectGroup, EFFECT_NONE, ClientEffect, GestureEffect } from 'src/types/Deco';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { STATUS } from 'src/types/Match';
import { ModalType } from 'src/types/Modal';
import { ToastType } from 'src/types/Toast';
import { WebToAppModalType } from 'src/types/WebToApp';
import { getResourceWithCache } from 'src/utils/cache';
import getDeviceInfo from 'src/utils/device/info';
import { pushEvent } from 'src/utils/event-gateway';
import { getAtomWithStorage } from 'src/utils/localStorageUtils';
import EffectRendererPipe from 'src/utils/media/pipes/effect';
import { getDecoEffectListAPI, reportInMatchEffectUsageAPI } from './api';
const EffectOffToastIcon = styled(StudioGraphicFillIcon)`
  color: ${({
  theme
}) => theme.color.contentDeep};
`;
export const isDecoSupportedAtom = atom(true);
const baseActiveEffectsAtom = getAtomWithStorage<DecoEffect[]>('activeEffects', [EFFECT_NONE]);

/**
 * 이펙트가 적용되지 않은 경우 [EFFECT_NONE] 요소 하나가 존재해야하기 때문에
 * atom composing으로 방어 로직 포함된 setter function 적용
 */
export const activeEffectsAtom = atom(get => get(baseActiveEffectsAtom), (get, set, update: DecoEffect[] | ((prev: DecoEffect[]) => DecoEffect[])) => {
  const updatedActiveEffects = typeof update === 'function' ? update(get(baseActiveEffectsAtom)) : update;
  set(baseActiveEffectsAtom, updatedActiveEffects.length ? updatedActiveEffects : [EFFECT_NONE]);
});
export const isApplyingEffectAtom = atom(get => {
  const activeEffects = get(activeEffectsAtom);
  // 이펙트가 적용되지 않았을 때에도 [EFFECT_NONE]의 상태라 발생 가능하지 않지만 방어 로직으로 작성
  if (!activeEffects.length) {
    Sentry.captureMessage('Active effect is empty while it should have more than one element');
    return false;
  }
  return activeEffects.length > 1 || activeEffects[0].effectId !== EFFECT_NONE.effectId;
});
export const isLoadingEffectRendererPipeAtom = atom(false);
export const loadingEffectIdSetAtom = atom<Set<DecoEffect['effectId']>>(new Set<DecoEffect['effectId']>());
const clampBeautyStrength = (value: number) => Math.min(1, Math.max(0, value));
export const DEFAULT_BEAUTY_EFFECT_STRENGTH = 0;
type BeautyEffectStrengthMap = Record<string, number>;
export const beautyEffectStrengthsAtom = getAtomWithStorage<BeautyEffectStrengthMap>('beautyEffectStrengths', {});
export const selectedBeautyEffectAtom = atom<BeautyEffect | null>(null);
export const getBeautyEffectStrength = (strengths: BeautyEffectStrengthMap, effectId: string) => {
  const stored = strengths[effectId];
  if (typeof stored !== 'number' || Number.isNaN(stored)) {
    return DEFAULT_BEAUTY_EFFECT_STRENGTH;
  }
  return clampBeautyStrength(stored);
};
const markEffectAsLoadingAtom = atom(null, (get, set, effect: DecoEffect) => {
  set(loadingEffectIdSetAtom, prev => new Set(prev.add(effect.effectId)));
});
const unMarkEffectAsLoadingAtom = atom(null, (get, set, effect: DecoEffect) => {
  set(loadingEffectIdSetAtom, prev => {
    const newSet = new Set(prev);
    newSet.delete(effect.effectId);
    return newSet;
  });
});

/**
 * 이펙트 관련 최소 하나의 요소가 로딩중임을 의미
 */
export const isLoadingEffectAtom = atom(get => get(isLoadingEffectRendererPipeAtom) || get(loadingEffectIdSetAtom).size > 0);
export const userEffectSettingAtom = atom(get => {
  const loadingEffectIdSet = get(loadingEffectIdSetAtom);
  const beautyEffectStrengths = get(beautyEffectStrengthsAtom);
  const activeEffectIds = get(activeEffectsAtom).filter(activeEffect => {
    return activeEffect.effectId !== EFFECT_NONE.effectId;
  }).map(activeEffect => activeEffect.effectId);

  // 최종적으로 적용이 완료된 이펙트만을 포함
  const loadedActiveEffectIds = activeEffectIds.filter(activeEffectId => {
    return !loadingEffectIdSet.has(activeEffectId);
  });
  const loadedActiveBeautyEffects = get(activeEffectsAtom).filter(effect => effect.effectId !== EFFECT_NONE.effectId && !loadingEffectIdSet.has(effect.effectId)).filter(isBeautyEffect);
  return {
    effectIdList: loadedActiveEffectIds,
    decoEffectStrengths: loadedActiveBeautyEffects.map(effect => ({
      effectId: effect.effectId,
      strength: getBeautyEffectStrength(beautyEffectStrengths, effect.attributes?.effectRenderer[0] || ''),
      defaultStrength: effect.defaultValue
    })),
    facialRecognitionValue: 0
  };
});
export const decoListAtom = atom<DecoEffectGroup[]>([]);
export const effectsAtom = atom<DecoEffect[]>(get => {
  return get(decoListAtom).map(effectGroup => effectGroup.effects).flat();
});

/**
 * key: effectId
 */
export const effectGroupMapAtom = atom<Map<string, DecoGroupName>>(new Map());
export const gestureEffectStringGuideQueueAtom = atom<GestureEffect['effectId'][]>([]);
export const sendGestureEffectTriggerEventAtom = atom(null, (get, set, effect: DecoEffect) => {
  const matchInfo = get(matchInfoAtom);
  pushEvent({
    eventType: EVENT_TYPE.DECO,
    eventName: EVENT_NAME.MIRROR__REPORT_OPERATE_GESTURE,
    eventParams: {
      action_category: 'report',
      tab: 'mirror',
      page: get(eventMatchPageAtom),
      target: 'operate_gesture',
      effect_id: effect.effectId,
      match_id: matchInfo?.matchId ?? null
    }
  });
});
export const sendGestureEffectToggleEventAtom = atom(null, (get, set, effect: DecoEffect, isToggledOn: boolean, reason: 'click' | 'othereffect' | 'removeeffect') => {
  const matchInfo = get(matchInfoAtom);
  const isCreatorMode = get(isCreatorModeAtom);
  pushEvent({
    eventType: EVENT_TYPE.DECO,
    eventName: EVENT_NAME.MIRROR__REPORT_GESTURE_TOGGLE,
    eventParams: {
      action_category: 'report',
      tab: 'mirror',
      page: get(eventMatchPageAtom),
      target: 'gesture_toggle',
      reason,
      effect_id: effect.effectId,
      status: isToggledOn ? 'on' : 'off',
      match_id: matchInfo?.matchId ?? null,
      position: isCreatorMode ? 'creator_sidetoolbar_in' : 'creator_sidetoolbar_outside'
    }
  });
});
export const sendGestureEffectOpenModalEventAtom = atom(null, get => {
  const isCreatorMode = get(isCreatorModeAtom);
  pushEvent({
    eventType: EVENT_TYPE.DECO,
    eventName: EVENT_NAME.MIRROR__OPEN_GESTURE_EFFECT,
    eventParams: {
      action_category: 'open',
      tab: 'mirror',
      page: get(eventMatchPageAtom),
      target: 'gesture_modal',
      position: isCreatorMode ? 'creator_sidetoolbar_in' : 'creator_sidetoolbar_outside'
    }
  });
});
export const handleDecoEffectLoadCompleteAtom = atom(null, (get, set, effect: DecoEffect) => {
  set(unMarkEffectAsLoadingAtom, effect);
  const matchId = get(matchInfoAtom)?.matchId;
  if (get(statusAtom) === STATUS.MATCHED && matchId) {
    reportInMatchEffectUsageAPI(matchId, get(userEffectSettingAtom));
  }
});
export const activateEffectAtom = atom(null, (get, set, effect: DecoEffect) => {
  const activeEffects = get(activeEffectsAtom);
  if (isGestureEffect(effect)) {
    set(gestureEffectStringGuideQueueAtom, prev => {
      return [...prev, effect.effectId];
    });
  }
  if (activeEffects.some(activeEffect => activeEffect.effectId === effect.effectId)) return;
  const effectGroupMap = get(effectGroupMapAtom);
  const effectGroupName = effectGroupMap.get(effect.effectId);
  if (!effectGroupName) return;
  let updatedActiveEffects: DecoEffect[] = [effect];
  if (effectGroupName === DecoGroupName.BEAUTY) {
    const compatibleWithPrevEffects = activeEffects.filter(activeEffect => {
      const activeEffectGroupName = effectGroupMap.get(activeEffect.effectId);
      if (!activeEffectGroupName) return;
      return activeEffectGroupName === effectGroupName;
    });
    updatedActiveEffects = [...updatedActiveEffects, ...compatibleWithPrevEffects];
  } else {
    set(selectedBeautyEffectAtom, null);
  }
  const deactivatedEffects = activeEffects.filter(activeEffect => {
    return updatedActiveEffects.every(updatedActiveEffect => updatedActiveEffect.effectId !== activeEffect.effectId);
  });
  deactivatedEffects.forEach(deactivatedEffect => {
    EffectRendererPipe.removeEffect(deactivatedEffect);
    if (isGestureEffect(deactivatedEffect)) {
      set(sendGestureEffectToggleEventAtom, deactivatedEffect, false, effect.effectId === EFFECT_NONE.effectId ? 'removeeffect' : 'othereffect');
    }
  });
  set(activeEffectsAtom, updatedActiveEffects);
  updatedActiveEffects.forEach(updatedActiveEffect => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    set(handleDecoDownloadEffectAtom, updatedActiveEffect);
  });
});
export const deactivateEffectAtom = atom(null, (get, set, effect: DecoEffect) => {
  const activeEffects = get(activeEffectsAtom);
  const updatedActiveEffects = activeEffects.filter(activeEffect => activeEffect.effectId !== effect.effectId);
  if (updatedActiveEffects.length !== activeEffects.length) {
    EffectRendererPipe.removeEffect(effect);
    set(activeEffectsAtom, updatedActiveEffects);
  }
  if (isGestureEffect(effect)) {
    set(gestureEffectStringGuideQueueAtom, prev => {
      return prev.filter(gestureEffectId => gestureEffectId !== effect.effectId);
    });
  }
});
export const removeDecoEffectAtom = atom(null, (get, set) => {
  set(loadingEffectIdSetAtom, new Set());
  set(selectedBeautyEffectAtom, null);
  set(activateEffectAtom, EFFECT_NONE);
});
export const handleUnsupportedDecoAccessAtom = atom(null, (get, set) => {
  const {
    isMobile,
    isWebKit
  } = getDeviceInfo();

  // WEB-4805: iOS 모바일에서는 web_to_app 모달을 노출하지 않음 (iOS 리뷰 정책 대응, 추후 원복 가능)
  if (isMobile && !isWebKit) {
    set(showModalAtom, {
      key: ModalType.WEB_TO_APP,
      component: () => <WebToAppModal type={WebToAppModalType.EFFECT} />
    });
  } else {
    set(showToastAtom, {
      type: ToastType.ERROR,
      message: 'deco_studio_error_not_supported'
    });
  }
});
export const handleDecoUnsupportAtom = atom(null, (get, set, unsupportReason?: DecoUnsupportReason) => {
  const decoWasPreviouslyAvailable = get(isDecoSupportedAtom);
  set(isDecoSupportedAtom, false);

  // 이펙트가 처음으로 비활성화 되었을 때에만 이벤트 로그 전송
  if (decoWasPreviouslyAvailable && unsupportReason !== undefined) {
    pushEvent({
      eventType: EVENT_TYPE.DECO,
      eventName: EVENT_NAME.MIRROR_REPORT_FAIL_EFFECT,
      eventParams: {
        action_category: 'report',
        tab: 'mirror',
        page: get(eventMatchPageAtom),
        target: 'fail_effect',
        reason: unsupportReason
      }
    });
  }
  const isDecoModalOpen = get(openedModalsAtom).some(modal => modal.key === ModalType.EFFECTS);

  // 데코 스튜디오 모달이 켜져 있거나 현재 적용 혹은 시도중인 이펙트가 있는 경우
  if (isDecoModalOpen || get(isApplyingEffectAtom)) {
    set(showToastAtom, {
      type: ToastType.ERROR,
      message: 'deco_studio_error_not_supported'
    });
  }
  set(removeDecoEffectAtom);
});

/**
 * 이펙트를 적용하는 과정에서 예상치 못한 이슈가 발생하여 실제로 해당 이펙트가 유저에게 적용되지 못하는 경우
 */
export const handleUnexpectedEffectFailAtom = atom(null, (get, set, failedEffect: DecoEffect) => {
  set(unMarkEffectAsLoadingAtom, failedEffect);
  set(showCommonErrorToastAtom);
  set(activeEffectsAtom, prev => prev.filter(activeEffect => activeEffect.effectId !== failedEffect.effectId));
});
const hasShownEffectToastAtom = getAtomWithStorage('hasShownEffectToast', false);
const showEffectToastOnceAtom = atom(null, (get, set) => {
  if (get(hasShownEffectToastAtom)) {
    return;
  }
  set(showToastAtom, {
    message: i18n?.t('deco_studio_info_gesture_setting') ?? '',
    type: ToastType.INFO,
    Icon: EffectOffToastIcon,
    duration: 5_000
  });
  set(hasShownEffectToastAtom, true);
});
export const handleDecoDownloadEffectAtom = atom(null, async (get, set, effect: DecoEffect) => {
  if (!get(isDecoSupportedAtom) || !get(decoListAtom).length) return;
  if (!effect.attributes?.effectRenderer) return;
  set(markEffectAsLoadingAtom, effect);
  let activeResource: ArrayBuffer | null = null;
  if (effect.resource?.resourceUrl) {
    let encryptedResource;
    try {
      encryptedResource = await getResourceWithCache(effect.resource.resourceUrl, 'arraybuffer');
    } catch {
      set(showToastAtom, {
        type: ToastType.ERROR,
        message: 'deco_studio_error_network'
      });
      set(unMarkEffectAsLoadingAtom, effect);
      pushEvent({
        eventType: EVENT_TYPE.DECO,
        eventName: EVENT_NAME.MIRROR_REPORT_NETWORK_FAIL_EFFECT,
        eventParams: {
          action_category: 'report',
          tab: 'mirror',
          page: get(eventMatchPageAtom),
          target: 'network_fail_effect'
        }
      });
      return;
    }
    activeResource = (await import(/* webpackChunkName: 'resourceDecryption' */'src/utils/resourceDecryption')).resourceDecrypt(encryptedResource, effect.resource?.encryptionKey || '').buffer as ArrayBuffer;
  }

  // 로딩 도중에 active effect가 변경되지 않았을 때에만 실제 스트림에 반영
  const isActive = get(activeEffectsAtom).some(activeEffect => activeEffect.effectId === effect.effectId);
  if (!isActive) return;
  const {
    isMobile,
    isWebKit
  } = getDeviceInfo();
  const isAndroid = isMobile && !isWebKit;
  const onEffectTrigger = isGestureEffect(effect) ? (event: EffectTriggerEvent) => {
    switch (event.type) {
      case 'animationStart':
        {
          set(sendGestureEffectTriggerEventAtom, effect);
          break;
        }
      case 'animationEnd':
        {
          if (!isAndroid) {
            // 안드로이드는 직접 킨 유저만 있기 때문에 최초 제스쳐 이펙트 적용 관련 안내 토스트 미제공
            set(showEffectToastOnceAtom);
          }
          break;
        }
    }
  } : undefined;
  EffectRendererPipe.setEffect(effect, activeResource, onEffectTrigger).then(result => {
    if (result === true) {
      set(handleDecoEffectLoadCompleteAtom, effect);
    } else if (result === false) {
      set(handleUnexpectedEffectFailAtom, effect);
    }
  });
  set(handleMyStreamVideoAtom);
  // 추가적인 로딩 작업이 필요하지 않는 경우
  if (!effect.resource) {
    set(handleDecoEffectLoadCompleteAtom, effect);
  }
});
export const loadEffectRendererPipeAtom = atom(null, async (get, set) => {
  if (get(isLoadingEffectRendererPipeAtom) || !get(isDecoSupportedAtom) || EffectRendererPipe.getIsInitialized()) {
    return;
  }
  set(isLoadingEffectRendererPipeAtom, true);
  const isInitSuccess = await EffectRendererPipe.initialize();
  if (!isInitSuccess) {
    /**
     * 이펙트 라이브러리 내부에서 미디어파이프 모델을 구동함에 따라 어느 모델이 실패했는지 구분 불가능해짐
     * 이벤트 로그 제외 에러 핸들링이 동일하고 두 모델의 구동 실패 환경이 크게 차이나지 않으므로 임시로 일관되게 처리
     * @see https://hpcnt.slack.com/archives/C02SQS5AJDR/p1726733760517309
     *
     * @TODO 이펙트 라이브러리에 에러 인터페이스 추가시 재검토
     */
    set(handleDecoUnsupportAtom, DecoUnsupportReason.MEDIAPIPE_LOAD_FAILED_SELFIE_SEGMENTATION);
  }
  set(isLoadingEffectRendererPipeAtom, false);
});
export const getDecoEffectListAtom = atom(null, async (get, set) => {
  const {
    data: {
      result: {
        effectGroups
      }
    }
  } = await getDecoEffectListAPI();
  const {
    isMobile,
    isWebKit,
    osMajorVersion
  } = getDeviceInfo();

  // 서버에서 내려주는 이펙트 중 클라이언트에서 인지하는 목록만 필터링
  const supportedEffectGroups = effectGroups.filter(effectGroup => {
    return Object.values(DecoGroupName).includes(effectGroup.groupId);
  }).map(effectGroup => {
    if (isGestureEffectGroup(effectGroup)) {
      // 일부 제스쳐 이펙트는 성능 이슈로 모바일 기기에서 제공 제한
      return {
        ...effectGroup,
        effects: effectGroup.effects.filter(effect => !(isMobile && effect.effectId === 'DECO_WEB_GESTURE_WAVE'))
      };
    }
    return effectGroup;
  });
  const effects = supportedEffectGroups.map(effectGroup => effectGroup.effects).flat();
  const baseBeautyEffect = supportedEffectGroups.find(effectGroup => effectGroup.groupId === DecoGroupName.BEAUTY)?.effects?.[0];

  // 캐싱된 기존 이펙트가 deprecated되지 않았는지 검사
  if (get(isApplyingEffectAtom)) {
    const cachedEffects = get(activeEffectsAtom);
    const updatedActiveEffects = cachedEffects.map(cachedEffect => {
      return effects.find(
      // 세부 설정 등 바뀌었을 경우 고려해서 동일 effect id여도 API로 가져온 것으로 적용
      effect => effect.effectId === cachedEffect.effectId);
    }).map((resolvedEffect, index) => {
      if (resolvedEffect) return resolvedEffect;
      const cachedEffect = cachedEffects[index];
      if (cachedEffect && isBeautyEffect(cachedEffect)) {
        return baseBeautyEffect ? {
          ...baseBeautyEffect,
          effectId: cachedEffect.effectId
        } : cachedEffect;
      }
      return undefined;
    })
    // deprecated effect (호환성 이슈 등에 의한 크래시 방지)
    .filter((cachedEffect): cachedEffect is DecoEffect => cachedEffect !== undefined);
    set(activeEffectsAtom, updatedActiveEffects);
  }

  // update effect group map
  const effectGroupMap = new Map();
  effectGroupMap.set(ClientEffect.NONE, DecoGroupName.ETC);
  supportedEffectGroups.forEach(effectGroup => {
    effectGroup.effects.forEach(effect => {
      effectGroupMap.set(effect.effectId, effectGroup.groupId);
    });
  });
  set(effectGroupMapAtom, effectGroupMap);
  set(decoListAtom, supportedEffectGroups);
  get(activeEffectsAtom).forEach(activeEffect => {
    if (isGestureEffect(activeEffect)) {
      set(gestureEffectStringGuideQueueAtom, prev => [...prev, activeEffect.effectId]);
    }
    set(handleDecoDownloadEffectAtom, activeEffect);
  });
  if (isMobile && !(isWebKit && osMajorVersion && osMajorVersion >= 26)) {
    return;
  }
  const beautyGroup = supportedEffectGroups.find(effectGroup => effectGroup.groupId === DecoGroupName.BEAUTY);
  const strengths = get(beautyEffectStrengthsAtom);
  if (!beautyGroup) {
    return;
  }
  beautyGroup.effects.forEach(effect => {
    if (!isBeautyEffect(effect)) {
      return;
    }
    const renderers = effect.attributes?.effectRenderer;
    const on = renderers?.reduce((prev, renderer) => {
      const currentStrength = strengths[renderer];
      if (currentStrength === undefined && effect.defaultValue) {
        strengths[renderer] = effect.defaultValue;
        return true;
      }
      return prev;
    }, false);
    set(beautyEffectStrengthsAtom, strengths);
    if (on) {
      set(activateEffectAtom, effect);
    }
  });
});